// frontend/src/hooks/useAnticipatoryLayout.ts
// TaisaCore Sprint 10: Anticipatory Layout Hook
// Time-based and role-based dashboard layout configuration

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  TimeOfDay,
  UserRole,
  LayoutConfig,
  LayoutFocus,
  AnticipatoryContextValue,
} from '../types/dashboard';

// ===== Time-of-Day Detection =====
const getTimeOfDay = (): TimeOfDay => {
  const hour = new Date().getHours();
  
  if (hour >= 6 && hour < 10) return 'morning';
  if (hour >= 10 && hour < 14) return 'midday';
  if (hour >= 14 && hour < 18) return 'afternoon';
  if (hour >= 18 && hour < 22) return 'evening';
  return 'night';
};

// ===== Hebrew Greetings by Time =====
const greetings: Record<TimeOfDay, { main: string; sub: string }> = {
  morning: {
    main: 'בוקר טוב! ☀️',
    sub: 'הנה מה שמחכה לך היום',
  },
  midday: {
    main: 'צהריים טובים! 🌤️',
    sub: 'בואו נבדוק מה בהמשך היום',
  },
  afternoon: {
    main: 'אחר הצהריים טובים! 🌅',
    sub: 'סיכום התורים שנותרו',
  },
  evening: {
    main: 'ערב טוב! 🌙',
    sub: 'הנה סיכום היום',
  },
  night: {
    main: 'לילה טוב! 🌙',
    sub: 'מוכנים ליום מחר?',
  },
};

// ===== Focus Area by Time =====
const focusByTime: Record<TimeOfDay, LayoutFocus> = {
  morning: 'preparation',
  midday: 'execution',
  afternoon: 'execution',
  evening: 'completion',
  night: 'review',
};

// ===== Widget Configurations by Role and Time =====
interface WidgetPriority {
  default: number;
  roles?: Partial<Record<UserRole, number>>;
  times?: Partial<Record<TimeOfDay, number>>;
}

const widgetPriorities: Record<string, WidgetPriority> = {
  // Today's Schedule
  'schedule-today': {
    default: 100,
    times: { morning: 150, midday: 120 },
    roles: { receptionist: 140, dentist: 130 },
  },
  // Pending Actions Feed
  'action-feed': {
    default: 90,
    times: { morning: 100, afternoon: 110 },
    roles: { manager: 130 },
  },
  // Waiting Room
  'waiting-room': {
    default: 80,
    times: { midday: 130, afternoon: 120 },
    roles: { receptionist: 150 },
  },
  // Current Patient
  'current-patient': {
    default: 70,
    times: { midday: 140, afternoon: 130 },
    roles: { dentist: 160, hygienist: 150 },
  },
  // Revenue Summary
  'revenue-summary': {
    default: 40,
    times: { evening: 120, night: 130 },
    roles: { manager: 140 },
  },
  // Staff Performance
  'staff-performance': {
    default: 30,
    times: { evening: 100, night: 110 },
    roles: { manager: 120 },
  },
  // Overdue Recalls
  'overdue-recalls': {
    default: 50,
    times: { morning: 80, night: 90 },
    roles: { receptionist: 90, manager: 85 },
  },
  // SHAAM Status
  'shaam-status': {
    default: 60,
    roles: { manager: 100, receptionist: 80 },
  },
  // AI Suggestions
  'ai-suggestions': {
    default: 75,
    roles: { dentist: 90, manager: 85 },
  },
  // Quick Stats
  'quick-stats': {
    default: 85,
    times: { morning: 95, evening: 110 },
    roles: { manager: 100 },
  },
};

// ===== Main Hook =====
export const useAnticipatoryLayout = (
  initialRole: UserRole = 'dentist'
): AnticipatoryContextValue => {
  const [userRole, setUserRole] = useState<UserRole>(initialRole);
  const [timeOverride, setTimeOverride] = useState<TimeOfDay | null>(null);
  const [expandedWidgets, setExpandedWidgets] = useState<Set<string>>(new Set());

  // Current time of day (auto-updates)
  const [currentTime, setCurrentTime] = useState<TimeOfDay>(getTimeOfDay());

  // Update time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(getTimeOfDay());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  // Effective time (with override support)
  const effectiveTime = timeOverride || currentTime;

  // Calculate widget priority
  const getWidgetPriority = useCallback(
    (widgetId: string): number => {
      const config = widgetPriorities[widgetId];
      if (!config) return 0;

      let priority = config.default;

      // Add role bonus
      if (config.roles?.[userRole]) {
        priority += config.roles[userRole]! - config.default;
      }

      // Add time bonus
      if (config.times?.[effectiveTime]) {
        priority += config.times[effectiveTime]! - config.default;
      }

      return priority;
    },
    [userRole, effectiveTime]
  );

  // Generate layout configuration
  const layout = useMemo((): LayoutConfig => {
    const greeting = greetings[effectiveTime];
    const focus = focusByTime[effectiveTime];

    // Sort widgets by priority
    const sortedWidgets = Object.keys(widgetPriorities)
      .map((id) => ({ id, priority: getWidgetPriority(id) }))
      .sort((a, b) => b.priority - a.priority);

    // Top 3 are priority widgets (expanded by default)
    const priorityWidgets = sortedWidgets.slice(0, 3).map((w) => w.id);

    // Next 3 are expanded
    const expanded = sortedWidgets.slice(3, 6).map((w) => w.id);

    // Rest are collapsed
    const collapsed = sortedWidgets.slice(6, 10).map((w) => w.id);

    // Very low priority are hidden
    const hidden = sortedWidgets.slice(10).map((w) => w.id);

    return {
      timeOfDay: effectiveTime,
      focus,
      greeting: greeting.main,
      greetingSubtext: greeting.sub,
      priorityWidgets,
      expandedWidgets: [...priorityWidgets, ...expanded, ...Array.from(expandedWidgets)],
      collapsedWidgets: collapsed.filter((id) => !expandedWidgets.has(id)),
      hiddenWidgets: hidden,
    };
  }, [effectiveTime, getWidgetPriority, expandedWidgets]);

  // Check if widget is expanded
  const isWidgetExpanded = useCallback(
    (widgetId: string): boolean => {
      return layout.expandedWidgets.includes(widgetId) || expandedWidgets.has(widgetId);
    },
    [layout.expandedWidgets, expandedWidgets]
  );

  // Toggle widget expansion
  const toggleWidget = useCallback((widgetId: string) => {
    setExpandedWidgets((prev) => {
      const next = new Set(prev);
      if (next.has(widgetId)) {
        next.delete(widgetId);
      } else {
        next.add(widgetId);
      }
      return next;
    });
  }, []);

  return {
    layout,
    userRole,
    setUserRole,
    timeOverride,
    setTimeOverride,
    isWidgetExpanded,
    toggleWidget,
    getWidgetPriority,
  };
};

export default useAnticipatoryLayout;
